"use client";

/**
 * highlight-glossary.tsx
 *
 * Takes a paragraph of plain text + a list of glossary terms and returns an
 * array of React nodes with the first occurrence of each matched term wrapped
 * in a <GlossaryTooltip>. Matching is case-insensitive, whole-word, and
 * longest-match-first so "Expense Ratio" wins over "Expense".
 *
 * Alias support: abbreviations like "AUM" also match "Assets Under Management"
 * and vice versa, all resolving to the same glossary entry.
 *
 * To avoid visual noise, each term is only highlighted **once per call**
 * (i.e. once per paragraph when called paragraph-by-paragraph).
 *
 * Used by: FundSection when rendering report content with glossary tooltips.
 */

import React from "react";
import { GlossaryTooltip } from "@/components/shared/glossary-tooltip";

export interface GlossaryEntry {
  id: string;
  term: string;
  slug: string;
  definitionSimple: string;
}

/**
 * Alias map: abbreviation ↔ full form. Both directions are generated
 * automatically so "AUM" matches "Assets Under Management" and vice versa.
 * Only add entries where the glossary term itself is the abbreviation OR
 * the full form — not both (that would be two glossary entries).
 */
const ALIASES: Record<string, string[]> = {
  // term (as stored in glossary) → alternate surface forms to also match
  "AUM": ["Assets Under Management"],
  "NAV": ["Net Asset Value"],
  "SIP": ["Systematic Investment Plan"],
  "CAGR": ["Compound Annual Growth Rate"],
  "ELSS": ["Equity Linked Savings Scheme", "Equity Linked Saving Scheme"],
  "LTCG": ["Long Term Capital Gains", "Long-Term Capital Gains"],
  "STCG": ["Short Term Capital Gains", "Short-Term Capital Gains"],
  "IDCW": ["Income Distribution cum Capital Withdrawal"],
  "NFO": ["New Fund Offer"],
  "XIRR": ["Extended Internal Rate of Return"],
  "TDS": ["Tax Deducted at Source"],
  "EPS": ["Earnings Per Share"],
  "ROE": ["Return on Equity"],
  "ROIC": ["Return on Invested Capital"],
  "Expense Ratio": ["TER", "Total Expense Ratio"],
  "P/E Ratio": ["PE Ratio", "Price to Earnings", "Price-to-Earnings"],
  "Sharpe Ratio": ["Sharpe"],
  "Sortino Ratio": ["Sortino"],
  "Standard Deviation": ["Std Dev", "StdDev"],
  "R-Squared": ["R²", "R-squared", "R Squared"],
  "Market Capitalisation": ["Market Cap", "Mcap"],
  "Debt-to-Equity Ratio": ["D/E Ratio", "Debt to Equity"],
  "Section 80C": ["80C"],
  "Alpha": ["Jensen's Alpha"],
  "Beta": ["Market Beta"],
};

/**
 * Escape regex metachars in a user-provided string so we can safely embed
 * the glossary term into a RegExp.
 */
function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

type MatchTarget = {
  pattern: string; // the surface form to match
  entry: GlossaryEntry; // resolves to this glossary entry
};

/**
 * Highlight glossary terms inside a paragraph. Returns a React fragment list.
 */
export function highlightGlossaryTerms(
  text: string,
  terms: GlossaryEntry[]
): React.ReactNode[] {
  if (!text || terms.length === 0) return [text];

  // Build match targets: each glossary term + its aliases all resolve to the same entry
  const targets: MatchTarget[] = [];
  const termLookup = new Map<string, GlossaryEntry>();

  for (const entry of terms) {
    termLookup.set(entry.term.toLowerCase(), entry);
    targets.push({ pattern: entry.term, entry });

    // Add aliases for this term
    const aliases = ALIASES[entry.term];
    if (aliases) {
      for (const alias of aliases) {
        targets.push({ pattern: alias, entry });
      }
    }

    // Also check reverse: if an alias key matches this entry's term in lowercase
    // (already covered by the direct lookup above)
  }

  // Sort targets by pattern length desc so "Expense Ratio" matches before "Expense"
  targets.sort((a, b) => b.pattern.length - a.pattern.length);

  // Build a single case-insensitive regex with word boundaries
  const pattern = targets
    .map((t) => `\\b${escapeRegex(t.pattern)}\\b`)
    .join("|");
  const regex = new RegExp(`(${pattern})`, "i");

  // Build a quick lookup from lowercase pattern → entry
  const patternToEntry = new Map<string, GlossaryEntry>();
  for (const t of targets) {
    patternToEntry.set(t.pattern.toLowerCase(), t.entry);
  }

  const used = new Set<string>(); // slugs already highlighted in this call
  const nodes: React.ReactNode[] = [];
  let remaining = text;
  let keyCounter = 0;

  while (remaining.length > 0) {
    const match = remaining.match(regex);
    if (!match || match.index === undefined) {
      nodes.push(remaining);
      break;
    }

    // Find which entry matched (case-insensitive)
    const matchedText = match[0];
    const entry = patternToEntry.get(matchedText.toLowerCase());

    // Push prefix text
    if (match.index > 0) {
      nodes.push(remaining.slice(0, match.index));
    }

    if (entry && !used.has(entry.slug)) {
      used.add(entry.slug);
      nodes.push(
        <GlossaryTooltip
          key={`gl-${entry.slug}-${keyCounter++}`}
          term={entry.term}
          slug={entry.slug}
          definition={entry.definitionSimple}
        >
          {matchedText}
        </GlossaryTooltip>
      );
    } else {
      // Already used or unmatched — push as plain text
      nodes.push(matchedText);
    }

    remaining = remaining.slice(match.index + matchedText.length);
  }

  return nodes;
}
